import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { BILANS, INTERSECTIONS_GRILLE } from '../data/bilansEleve';
import { ArrowLeft, CheckCircle2, Sparkles, Target, ListChecks, ShieldCheck, Clock, BookOpenCheck, RotateCcw, Trophy, AlertTriangle, XCircle } from 'lucide-react';

const COLOR_HEADER = {
  sky: 'bg-sky-600 shadow-sky-900/40',
  amber: 'bg-amber-500 shadow-amber-900/40',
  red: 'bg-red-600 shadow-red-900/40',
  emerald: 'bg-emerald-600 shadow-emerald-900/40',
};

const TAB_ACTIVE = {
  sky: 'bg-sky-600 text-white',
  amber: 'bg-amber-500 text-amber-950',
  red: 'bg-red-600 text-white',
  emerald: 'bg-emerald-600 text-white',
};

const STATES_GRID = [
  { v: 'ok', l: 'Acquis', cls: 'bg-emerald-600 text-white border-emerald-500', icon: CheckCircle2 },
  { v: 'ameliorer', l: 'À améliorer', cls: 'bg-amber-500 text-amber-950 border-amber-400', icon: AlertTriangle },
  { v: 'non_acquis', l: 'Non acquis', cls: 'bg-red-600 text-white border-red-500', icon: XCircle },
];

function IntersectionsInteractive() {
  const [grid, setGrid] = useState({});
  const setOne = (id, v) => setGrid((p) => ({ ...p, [id]: v }));
  const stats = useMemo(() => {
    const c = { ok: 0, ameliorer: 0, non_acquis: 0 };
    Object.values(grid).forEach((v) => { if (c[v] !== undefined) c[v]++; });
    return c;
  }, [grid]);
  const total = INTERSECTIONS_GRILLE.length;
  const evalCount = stats.ok + stats.ameliorer + stats.non_acquis;
  const pct = total ? Math.round((stats.ok / total) * 100) : 0;
  const reset = () => { if (window.confirm('Réinitialiser la grille ?')) setGrid({}); };

  let verdict, vColor;
  if (evalCount === 0) { verdict = 'Évalue les intersections pour obtenir un verdict.'; vColor = 'border-white/10 text-white/60'; }
  else if (stats.non_acquis > 3) { verdict = 'Plusieurs intersections non acquises — à retravailler en priorité.'; vColor = 'border-red-700/40 text-red-300 from-red-700/20'; }
  else if (pct >= 80) { verdict = 'Très bon niveau · prêt(e) pour l\'examen côté intersections.'; vColor = 'border-emerald-700/40 text-emerald-300 from-emerald-700/20'; }
  else if (pct >= 60) { verdict = 'Niveau correct · cible les "À améliorer" pour consolider.'; vColor = 'border-sky-700/40 text-sky-300 from-sky-700/20'; }
  else { verdict = 'Niveau insuffisant · continue à pratiquer les carrefours.'; vColor = 'border-amber-700/40 text-amber-300 from-amber-700/20'; }

  return (
    <div data-testid="intersections-grille">
      {/* Stats top */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Évalué</div>
          <div className="font-serif-editorial text-white text-[26px] leading-none mt-1">{evalCount}<span className="text-white/30 text-[14px]">/{total}</span></div>
        </div>
        <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-emerald-300 font-bold font-mono-spec">Acquis</div>
          <div className="font-serif-editorial text-emerald-400 text-[26px] leading-none mt-1">{stats.ok}</div>
        </div>
        <div className="rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">À améliorer</div>
          <div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{stats.ameliorer}</div>
        </div>
        <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Non acquis</div>
          <div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.non_acquis}</div>
        </div>
      </div>

      {/* Verdict */}
      <div className={`rounded-xl border-2 bg-gradient-to-br to-transparent p-4 mb-5 ${vColor}`} data-testid="grille-verdict">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-bold font-mono-spec">
          <Trophy size={12} /> Verdict provisoire · {pct} % acquis
        </div>
        <p className="text-white/85 text-[14px] mt-2 leading-relaxed">{verdict}</p>
      </div>

      {/* Grille */}
      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
        <div className="hidden md:grid grid-cols-[80px_1fr_auto] gap-3 px-4 py-2.5 bg-white/[0.03] text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">
          <div>Picto</div>
          <div>Intersection</div>
          <div>Évaluation</div>
        </div>
        <ul className="divide-y divide-white/5">
          {INTERSECTIONS_GRILLE.map((it) => {
            const cur = grid[it.id];
            return (
              <li key={it.id} className="grid md:grid-cols-[80px_1fr_auto] gap-3 px-4 py-3 items-center" data-testid={`row-${it.id}`}>
                <div className="text-red-400 font-mono-spec text-[13px] font-bold text-center md:text-left">{it.pic}</div>
                <div className="text-white text-[13.5px] leading-snug">{it.label}</div>
                <div className="flex gap-1.5 flex-wrap">
                  {STATES_GRID.map((s) => {
                    const I = s.icon;
                    const active = cur === s.v;
                    return (
                      <button key={s.v} onClick={() => setOne(it.id, active ? null : s.v)} data-testid={`row-${it.id}-${s.v}`} className={`h-8 px-2.5 rounded-md border text-[11px] font-bold inline-flex items-center gap-1 transition ${active ? s.cls + ' shadow-md' : 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white'}`}>
                        <I size={11} /> {s.l}
                      </button>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[11.5px] text-white/40 font-mono-spec">{total} types d'intersections · grille codevoiture.fr</div>
        <button onClick={reset} data-testid="grille-reset" className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[12.5px] font-semibold transition">
          <RotateCcw size={13} /> Réinitialiser la grille
        </button>
      </div>
    </div>
  );
}

function SectionedGrille({ sections }) {
  const [grid, setGrid] = useState({});
  const setOne = (id, v) => setGrid((p) => ({ ...p, [id]: v }));
  const allItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const stats = useMemo(() => {
    const c = { ok: 0, ameliorer: 0, non_acquis: 0 };
    Object.values(grid).forEach((v) => { if (c[v] !== undefined) c[v]++; });
    return c;
  }, [grid]);
  const total = allItems.length;
  const evalCount = stats.ok + stats.ameliorer + stats.non_acquis;
  const pct = total ? Math.round((stats.ok / total) * 100) : 0;
  const reset = () => { if (window.confirm('Réinitialiser ?')) setGrid({}); };

  let verdict, vColor;
  if (evalCount === 0) { verdict = 'Évalue les savoirs pour obtenir un verdict.'; vColor = 'border-white/10 text-white/60'; }
  else if (stats.non_acquis > 3) { verdict = 'Plusieurs savoirs non acquis — à retravailler en priorité.'; vColor = 'border-red-700/40 text-red-300 from-red-700/20'; }
  else if (pct >= 80) { verdict = 'Compétence acquise · prêt(e) pour la suivante.'; vColor = 'border-emerald-700/40 text-emerald-300 from-emerald-700/20'; }
  else if (pct >= 60) { verdict = 'Niveau correct · consolide les "À améliorer".'; vColor = 'border-sky-700/40 text-sky-300 from-sky-700/20'; }
  else { verdict = 'Niveau insuffisant · continue la pratique.'; vColor = 'border-amber-700/40 text-amber-300 from-amber-700/20'; }

  return (
    <div data-testid="sectioned-grille">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-3.5 text-center"><div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Évalué</div><div className="font-serif-editorial text-white text-[26px] leading-none mt-1">{evalCount}<span className="text-white/30 text-[14px]">/{total}</span></div></div>
        <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-3.5 text-center"><div className="text-[10.5px] uppercase tracking-wider text-emerald-300 font-bold font-mono-spec">Acquis</div><div className="font-serif-editorial text-emerald-400 text-[26px] leading-none mt-1">{stats.ok}</div></div>
        <div className="rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 text-center"><div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">À améliorer</div><div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{stats.ameliorer}</div></div>
        <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-3.5 text-center"><div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Non acquis</div><div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.non_acquis}</div></div>
      </div>
      <div className={`rounded-xl border-2 bg-gradient-to-br to-transparent p-4 mb-5 ${vColor}`} data-testid="sectioned-verdict">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-bold font-mono-spec"><Trophy size={12} /> Verdict provisoire · {pct} % acquis</div>
        <p className="text-white/85 text-[14px] mt-2 leading-relaxed">{verdict}</p>
      </div>
      <div className="space-y-5">
        {sections.map((s) => (
          <div key={s.section} className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
            <div className="px-4 py-3 bg-red-600/10 border-b border-red-700/30"><div className="text-[12.5px] text-red-300 font-bold font-mono-spec uppercase tracking-wider">{s.section}</div></div>
            <ul className="divide-y divide-white/5">
              {s.items.map((it) => {
                const cur = grid[it.code];
                return (
                  <li key={it.code} className="grid md:grid-cols-[60px_1fr_auto] gap-3 px-4 py-3 items-center" data-testid={`grille-row-${it.code}`}>
                    <div className="text-red-400 font-mono-spec text-[12.5px] font-bold">{it.code}</div>
                    <div className="text-white text-[13.5px] leading-snug">{it.label}</div>
                    <div className="flex gap-1.5 flex-wrap">
                      {STATES_GRID.map((s2) => {
                        const I = s2.icon;
                        const active = cur === s2.v;
                        return (<button key={s2.v} onClick={() => setOne(it.code, active ? null : s2.v)} className={`h-8 px-2.5 rounded-md border text-[11px] font-bold inline-flex items-center gap-1 transition ${active ? s2.cls + ' shadow-md' : 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white'}`}><I size={11} /> {s2.l}</button>);
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[11.5px] text-white/40 font-mono-spec">{total} savoirs · grille codevoiture.fr</div>
        <button onClick={reset} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[12.5px] font-semibold transition"><RotateCcw size={13} /> Réinitialiser</button>
      </div>
    </div>
  );
}


function BilanContent({ b }) {
  if (b.interactive && b.sections) {
    return (
      <article data-testid={`bilan-${b.code}`}>
        <div className="rounded-2xl border border-red-700/30 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-6 lg:p-8 mb-6">
          <div className="flex items-start gap-5">
            <div className={`shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-2xl text-white font-serif-editorial text-[26px] lg:text-[32px] flex items-center justify-center shadow-lg ${COLOR_HEADER[b.color]}`}>{b.code}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1">Grille d'évaluation REMC · {b.sections.reduce((a, s) => a + s.items.length, 0)} savoirs</div>
              <h2 className="font-serif-editorial text-white text-[26px] lg:text-[32px] leading-[1.05]">{b.title}</h2>
              <p className="text-white/70 text-[13.5px] mt-3 leading-relaxed">{b.objectif}</p>
            </div>
          </div>
        </div>
        <SectionedGrille sections={b.sections} />
      </article>
    );
  }
  if (b.interactive) {
    return (
      <article data-testid={`bilan-${b.code}`}>
        <div className="rounded-2xl border border-red-700/30 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-6 lg:p-8 mb-6">
          <div className="flex items-start gap-5">
            <div className={`shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-2xl text-white font-serif-editorial text-[18px] flex items-center justify-center shadow-lg ${COLOR_HEADER[b.color]}`}>
              FOCUS
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1">Grille d'évaluation interactive</div>
              <h2 className="font-serif-editorial text-white text-[26px] lg:text-[32px] leading-[1.05]">{b.title}</h2>
              <p className="text-white/70 text-[13.5px] mt-3 leading-relaxed">{b.objectif}</p>
            </div>
          </div>
        </div>
        <IntersectionsInteractive />
      </article>
    );
  }
  return (
    <article data-testid={`bilan-${b.code}`}>
      {/* Header bilan */}
      <div className="rounded-2xl border border-red-700/30 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-6 lg:p-8 mb-6">
        <div className="flex items-start gap-5">
          <div className={`shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-2xl text-white font-serif-editorial text-[26px] lg:text-[32px] flex items-center justify-center shadow-lg ${COLOR_HEADER[b.color]}`}>
            {b.code}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1">Bilan synthétique REMC</div>
            <h2 className="font-serif-editorial text-white text-[26px] lg:text-[32px] leading-[1.05]">{b.title}</h2>
            <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70 text-[11.5px] font-mono-spec">
              <Clock size={11} /> {b.duree}
            </div>
          </div>
        </div>
      </div>

      {/* Objectif */}
      <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5 mb-4">
        <div className="flex items-start gap-3">
          <Target size={16} className="text-red-400 shrink-0 mt-1" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-400 font-bold font-mono-spec mb-1.5">Objectif global</div>
            <p className="text-white/85 text-[14.5px] leading-relaxed">{b.objectif}</p>
          </div>
        </div>
      </div>

      {/* Points-clés */}
      <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5 mb-4">
        <div className="flex items-start gap-3">
          <ListChecks size={16} className="text-emerald-400 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-400 font-bold font-mono-spec mb-2.5">Points-clés à maîtriser</div>
            <ul className="space-y-2">
              {b.points_cles.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px] leading-relaxed">
                  <CheckCircle2 size={13} className="text-emerald-400 shrink-0 mt-1" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Check-list */}
      <div className="rounded-xl border border-amber-700/30 bg-amber-950/10 p-5 mb-4">
        <div className="flex items-start gap-3">
          <BookOpenCheck size={16} className="text-amber-400 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-300 font-bold font-mono-spec mb-2.5">Check-list de validation</div>
            <p className="text-white/65 text-[12.5px] mb-3 italic">Coche mentalement chaque item. Si tu hésites sur un seul, retravaille-le avant de demander la validation à ton enseignant.</p>
            <ul className="space-y-2">
              {b.check_list.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px] leading-relaxed">
                  <span className="w-4 h-4 rounded border border-amber-400/50 mt-0.5 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Signes d'acquisition */}
      <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-5">
        <div className="flex items-start gap-3">
          <Sparkles size={16} className="text-red-400 shrink-0 mt-1" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-300 font-bold font-mono-spec mb-1.5">Comment savoir que c'est acquis</div>
            <p className="text-white/85 text-[14px] italic leading-relaxed">{b.signes_acquis}</p>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function BilansCompetencesPage() {
  const [active, setActive] = useState(BILANS[0].code);
  const current = BILANS.find((b) => b.code === active) || BILANS[0];

  return (
    <PageShell>
      <section className="max-w-4xl mx-auto px-4 lg:px-6 mt-8 sm:mt-10 mb-12 sm:mb-16" data-testid="bilans-eleve-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[11px] sm:text-[12px] text-white/50 font-mono-spec mb-5 sm:mb-6 flex-wrap">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/cours-interactifs" className="hover:text-white transition">Cours interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Bilans REMC C1→C4</span>
        </nav>

        {/* Hero */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-9 mb-6 sm:mb-8 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-red-600/10 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-3 sm:mb-4">
              <ShieldCheck size={13} className="text-red-400" />
              <span className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Bilans synthétiques · 4 compétences + Focus</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[24px] sm:text-[40px] lg:text-[48px] leading-[1.05] sm:leading-[1.02] tracking-tight">
              Sais-tu où tu en es<br />
              <span className="text-red-500">dans ta formation ?</span>
            </h1>
            <p className="text-white/70 text-[13.5px] sm:text-[15px] mt-4 sm:mt-5 max-w-2xl leading-relaxed">
              Pour chacune des 4 compétences REMC + un focus Intersections : objectif global, points-clés à maîtriser, check-list de validation et signes concrets que la compétence est acquise.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-5 sm:mb-6 overflow-x-auto hide-scroll" role="tablist">
          {BILANS.map((b) => (
            <button
              key={b.code}
              onClick={() => setActive(b.code)}
              data-testid={`tab-${b.code}`}
              className={`h-9 sm:h-10 px-3 sm:px-4 rounded-full font-bold text-[12px] sm:text-[13px] inline-flex items-center gap-1.5 sm:gap-2 transition border whitespace-nowrap ${active === b.code ? TAB_ACTIVE[b.color] + ' border-transparent shadow-md' : 'bg-white/5 border-white/10 text-white/65 hover:bg-white/10 hover:text-white'}`}
            >
              {b.code === 'FOCUS' ? '🎯 Focus' : b.code} {b.code !== 'FOCUS' && b.title.split(' ').slice(0, 2).join(' ')}
            </button>
          ))}
        </div>

        {/* Bilan actif */}
        <BilanContent b={current} />

        {/* Actions footer */}
        <div className="flex items-center justify-between flex-wrap gap-3 mt-10 pt-6 border-t border-white/5">
          <Link to="/cours/eleve/voiture" data-testid="back-cours-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
            <ArrowLeft size={14} /> 38 leçons REMC
          </Link>
          <Link to="/cours/eleve/examen-blanc" data-testid="cta-examen-blanc" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold shadow-lg shadow-red-900/40 transition">
            Faire l'examen blanc →
          </Link>
        </div>

        <div className="mt-10 text-[11.5px] text-white/40 font-mono-spec inline-flex items-center gap-2">
          <ShieldCheck size={11} /> Bilans alignés REMC · codevoiture.fr
        </div>
      </section>
    </PageShell>
  );
}
